// Máscaras de digitação e validadores de campos (contraparte de input dos
// formatadores de exibição em enums.js). Padrão: mascarar no estado do form
// e desmascarar (onlyDigits/moneyToNumber) ao montar o payload.

export const onlyDigits = (v) => String(v ?? '').replace(/\D/g, '');

/** Telefone BR progressivo: (11) 8765-4321 ou (11) 98765-4321. */
export const maskPhone = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

/** Válido = fixo (10 dígitos) ou celular (11 dígitos) com DDD. */
export const isValidPhone = (v) => {
  const d = onlyDigits(v);
  return d.length === 10 || d.length === 11;
};

export const maskCpf = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

export const maskCnpj = (v) => {
  const d = onlyDigits(v).slice(0, 14);
  return d
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

/** CPF até 11 dígitos; vira CNPJ quando passa disso. */
export const maskCpfCnpj = (v) => (onlyDigits(v).length > 11 ? maskCnpj(v) : maskCpf(v));

const cpfDigit = (digits, factor) => {
  const sum = digits.reduce((acc, d, i) => acc + d * (factor - i), 0);
  const rest = (sum * 10) % 11;
  return rest === 10 ? 0 : rest;
};

export const isValidCpf = (v) => {
  const d = onlyDigits(v);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const nums = d.split('').map(Number);
  return cpfDigit(nums.slice(0, 9), 10) === nums[9] && cpfDigit(nums.slice(0, 10), 11) === nums[10];
};

const cnpjDigit = (digits, weights) => {
  const sum = digits.reduce((acc, d, i) => acc + d * weights[i], 0);
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
};

export const isValidCnpj = (v) => {
  const d = onlyDigits(v);
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const nums = d.split('').map(Number);
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, ...w1];
  return cnpjDigit(nums.slice(0, 12), w1) === nums[12] && cnpjDigit(nums.slice(0, 13), w2) === nums[13];
};

export const isValidCpfCnpj = (v) => (onlyDigits(v).length > 11 ? isValidCnpj(v) : isValidCpf(v));

export const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v ?? '').trim());

/** Dinheiro pt-BR digitado em centavos: "123456" → "1.234,56". */
export const maskMoney = (v) => {
  const d = onlyDigits(v).slice(0, 12);
  if (!d) return '';
  return (Number(d) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** "1.234,56" → 1234.56 (null quando vazio). */
export const moneyToNumber = (v) => {
  const d = onlyDigits(v);
  return d ? Number(d) / 100 : null;
};

/** 1234.56 → "1.234,56" (pré-preencher forms com valor da API). */
export const numberToMoney = (n) =>
  n == null || n === ''
    ? ''
    : Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
